import { useState, useEffect, useCallback } from 'react';
import apiClient from '../config/axios';
import { ENV_CONFIG } from '../config/environment';

interface UseInventoryHighlightingReturn {
  hasPendingInventoryRequests: boolean;
  hasLowStockAlerts: boolean;
  loading: boolean;
  refresh: () => void;
}

export const useInventoryHighlighting = (): UseInventoryHighlightingReturn => {
  const [hasPendingInventoryRequests, setHasPendingInventoryRequests] = useState(false);
  const [hasLowStockAlerts, setHasLowStockAlerts] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchInventoryRequests = useCallback(async () => {
    try {
      const response = await apiClient.get('/inventory-requests', {
        params: {
          limit: 1000 // Get all requests to check status
        }
      });
      
      const requests = response.data.data || [];
      
      // Check if there are any requests with status other than 'delivered' or 'cancelled'
      const pendingRequests = requests.filter((request: any) => {
        const status = request.status?.toLowerCase();
        return status && status !== 'delivered' && status !== 'cancelled';
      });
      
      setHasPendingInventoryRequests(pendingRequests.length > 0);
    } catch (error) {
      console.error('Error fetching inventory requests:', error);
      setHasPendingInventoryRequests(false);
    }
  }, []);

  const fetchLowStockAlerts = useCallback(async () => {
    try {
      // Get current seller ID from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        setHasLowStockAlerts(false);
        return;
      }

      const user = JSON.parse(userStr);
      const sellerId = user.id;

      if (!sellerId) {
        setHasLowStockAlerts(false);
        return;
      }

      // Use our new seller-specific low stock alerts endpoint
      const response = await apiClient.get(`/branch-medicines/seller/${sellerId}/low-stock`, {
        params: { 
          threshold: ENV_CONFIG.LOW_STOCK_THRESHOLD, 
          pageSize: 1 // Just check if any alerts exist
        }
      });
      
      const alerts = response.data.data || [];
      setHasLowStockAlerts(alerts.length > 0);
    } catch (error) {
      console.error('Error fetching low stock alerts:', error);
      setHasLowStockAlerts(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchInventoryRequests(),
        fetchLowStockAlerts()
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchInventoryRequests, fetchLowStockAlerts]);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Set up periodic refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(refresh, 30000);
    return () => clearInterval(interval);
  }, [refresh]);

  return {
    hasPendingInventoryRequests,
    hasLowStockAlerts,
    loading,
    refresh
  };
};
